import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { T, BRAND } from "../data/content";
import { useLang } from "../context/LangContext";
import { useSiteData } from "../context/SiteDataContext";
import { WhatsAppIcon } from "../components/icons";

function ShopNav() {
  const { lang, toggle, tr } = useLang();
  const { brand } = useSiteData();
  return (
    <header className="sticky top-0 z-40 border-b border-[#2E2418] bg-[#0C0806]/85 backdrop-blur-xl">
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 lg:px-10">
        <a href="/" className="flex items-center gap-3" data-testid="shop-logo">
          <img src={BRAND.logo} alt="Slaly" className="h-11 w-auto rounded-md bg-[#F5ECDA] p-1 object-contain" />
        </a>
        <div className="flex items-center gap-3">
          <a href="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#B7A78C] transition-colors hover:text-[#F5ECDA]" data-testid="shop-back">
            <ArrowLeft className="h-4 w-4" /> {tr(T.products.back)}
          </a>
          <button onClick={toggle} className="text-xs tracking-[0.15em] text-[#B7A78C] hover:text-[#D4AF37]" data-testid="shop-lang-toggle">
            <span className={lang === "en" ? "text-[#D4AF37]" : ""}>EN</span>
            <span className="mx-1 opacity-40">/</span>
            <span className={lang === "fr" ? "text-[#D4AF37]" : ""}>FR</span>
          </button>
        </div>
      </div>
    </header>
  );
}

function ProductCard({ p, i }) {
  const { tr } = useLang();
  const { brand } = useSiteData();
  const msg = encodeURIComponent(`${tr(T.products.msg)} ${p.name} (£${p.price})`);
  const waLink = `https://wa.me/${brand.whatsapp}?text=${msg}`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group flex flex-col overflow-hidden rounded-lg border border-[#2E2418] bg-[#15100B]"
      data-testid={`product-card-${p.id}`}
    >
      <div className="relative aspect-square overflow-hidden bg-[#1C1610]">
        {p.image ? (
          <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        ) : (
          <div className="flex h-full items-center justify-center text-[#B7A78C]">—</div>
        )}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-serif text-xl text-[#F5ECDA]">{p.name}</h3>
        {p.description && <p className="mt-1 flex-1 text-sm leading-relaxed text-[#B7A78C]">{p.description}</p>}
        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="font-serif text-2xl text-[#D4AF37]">£{p.price}</span>
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-4 py-2.5 text-[11px] font-medium uppercase tracking-[0.12em] text-black transition-colors hover:bg-[#F3E5AB]"
            data-testid={`product-order-${p.id}`}
          >
            <WhatsAppIcon className="h-4 w-4" /> {tr(T.products.order)}
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function Products() {
  const { tr } = useLang();
  const { products } = useSiteData();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#0C0806] text-[#F5ECDA]">
      <ShopNav />
      <main className="mx-auto max-w-[1400px] px-6 py-16 lg:px-10 lg:py-24">
        <div className="mb-12 max-w-2xl">
          <div className="mb-5 flex items-center gap-3">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">{tr(T.products.label)}</span>
          </div>
          <h1 className="font-serif text-5xl tracking-tight md:text-6xl">{tr(T.products.title)}</h1>
          <p className="mt-5 text-base leading-relaxed text-[#B7A78C]">{tr(T.products.intro)}</p>
        </div>

        {products.length === 0 ? (
          <p className="py-20 text-center text-[#B7A78C]">{tr(T.products.empty)}</p>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="products-grid">
            {products.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
          </div>
        )}
      </main>
    </div>
  );
}
